/**
 * Nexus, the pure half (piece 25; decision 0251): the measurement window a
 * threshold is read over, the two-stage crossing test, the four positions a
 * state can hold, and the sentences the Warning and the Critical carry.
 *
 * 🔨 A threshold is two numbers and a measurement window, never one number: a
 * state can trigger on 200 transactions regardless of value.
 */
import type { StatusDefinitionRow } from '../statusDefinitions';
import { DEFAULT_CURRENCY_CODE, formatCurrency } from '../money';
import { calendarPartsInZone } from '../zonedTime';
import { counted } from '../composites/countedUnit';

export const NEXUS_MEASUREMENT_PERIODS = ['CurrentCalendarYear', 'PreviousCalendarYear'] as const;
export type NexusMeasurementPeriodValue = (typeof NEXUS_MEASUREMENT_PERIODS)[number];

export const NEXUS_REGISTRATION_STATUSES = ['NotRequired', 'Monitoring', 'ThresholdCrossed', 'Registered'] as const;
export type NexusRegistrationStatusValue = (typeof NEXUS_REGISTRATION_STATUSES)[number];

/**
 * 🔒 The four positions as `StatusDefinition` rows (decision 0706), which the
 * register's pill reads through the shell's one resolver (decision 0639). A
 * watched state is a wait, a crossing is something to act on, a registration
 * is settled well, and a state with no statewide sales tax is settled and out
 * of the way. 🔨 No screen types these colours; the migration seeds these rows
 * and the database suite holds the table to this list.
 */
export const NEXUS_REGISTRATION_STATUS_DEFINITIONS: readonly (StatusDefinitionRow & { statusValue: NexusRegistrationStatusValue })[] = [
  { entityType: 'NexusRegistrationStatus', statusValue: 'NotRequired', label: 'Not Required', colorGroup: 'inactive', sortOrder: 1 },
  { entityType: 'NexusRegistrationStatus', statusValue: 'Monitoring', label: 'Monitoring', colorGroup: 'warning', sortOrder: 2 },
  { entityType: 'NexusRegistrationStatus', statusValue: 'ThresholdCrossed', label: 'Threshold Crossed', colorGroup: 'error', sortOrder: 3 },
  { entityType: 'NexusRegistrationStatus', statusValue: 'Registered', label: 'Registered', colorGroup: 'good', sortOrder: 4 },
];

/** The two windows a threshold is measured over, as the register's own form names them. */
export const NEXUS_MEASUREMENT_PERIOD_LABELS: Readonly<Record<NexusMeasurementPeriodValue, string>> = {
  CurrentCalendarYear: 'The current calendar year',
  PreviousCalendarYear: 'The previous calendar year',
};

/** The Organization-scoped key the Nexus Notification Thresholds screen edits, shipping at 80 (decision 0251). */
export const NEXUS_WARNING_PERCENT_KEY = 'tax.nexusWarningPercent';
export const NEXUS_WARNING_PERCENT_DEFAULT = 80;

export function warningPercentProblem(value: unknown): string | null {
  const percent = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(percent)) return 'the warning percentage is a number';
  if (percent <= 0) return 'the warning percentage is above zero; a warning at nought fires on the first sale';
  if (percent >= 100) return 'the warning percentage is below 100; at 100 the warning and the crossing are the same event';
  return null;
}

/**
 * The window the rolling counters cover. The current calendar year starts on
 * 1 January of `asOf`'s year and runs to `asOf`'s own day; the previous one is
 * that whole earlier year, because a state testing last year's sales asks
 * whether the seller crossed inside it.
 *
 * 🔨 The year is `asOf`'s in the Organization's own zone, never UTC's (found
 * in review, 2026-09-15): a job at 02:00 UTC on 1 January is 16:00 on 31
 * December in Hawaii, and a UTC reading re-windows the counters a day early.
 */
export function measurementWindowStart(period: NexusMeasurementPeriodValue, asOf: Date, zone: string): Date {
  const { year } = calendarPartsInZone(asOf, zone);
  return new Date(Date.UTC(year - (period === 'PreviousCalendarYear' ? 1 : 0), 0, 1));
}

/**
 * The window's last day. 🔨 A previous-calendar-year test ends on 31 December
 * of that year (found in review, 2026-09-15): it ended on 1 January of the
 * current year, so a $500,000 invoice dated that day counted against the year
 * before it and could cross that year's threshold on its own.
 */
export function measurementWindowEnd(period: NexusMeasurementPeriodValue, asOf: Date, zone: string): Date {
  const { year, monthIndex, day } = calendarPartsInZone(asOf, zone);
  if (period === 'CurrentCalendarYear') return new Date(Date.UTC(year, monthIndex, day));
  return new Date(Date.UTC(year - 1, 11, 31));
}

export interface NexusThresholdFacts {
  /** Integer cents, or null where the state publishes no dollar test. */
  salesThreshold: number | null;
  /** A count, or null where the state publishes no transaction test. */
  transactionThreshold: number | null;
  measurementPeriod: NexusMeasurementPeriodValue;
}

export interface NexusCounters {
  rollingRevenue: number;
  rollingTransactionCount: number;
}

export type NexusStage = 'None' | 'Warning' | 'Critical';

/**
 * 🔨 Critical the moment either published test is met, Warning at the
 * configured early percentage of either. A state with an empty
 * `salesThreshold` and a set `transactionThreshold` crosses on count alone.
 */
export function nexusStage(counters: NexusCounters, threshold: NexusThresholdFacts | null, warningPercent: number): NexusStage {
  if (!threshold) return 'None';
  const tests: { measured: number; limit: number }[] = [];
  if (threshold.salesThreshold !== null && threshold.salesThreshold > 0) tests.push({ measured: counters.rollingRevenue, limit: threshold.salesThreshold });
  if (threshold.transactionThreshold !== null && threshold.transactionThreshold > 0) tests.push({ measured: counters.rollingTransactionCount, limit: threshold.transactionThreshold });
  if (tests.length === 0) return 'None';
  if (tests.some((t) => t.measured >= t.limit)) return 'Critical';
  if (tests.some((t) => t.measured >= (t.limit * warningPercent) / 100)) return 'Warning';
  return 'None';
}

/** How far through the nearer of the two tests the state is, as a percentage, for the register's own column. */
export function nexusProgressPercent(counters: NexusCounters, threshold: NexusThresholdFacts | null): number | null {
  if (!threshold) return null;
  const shares: number[] = [];
  if (threshold.salesThreshold !== null && threshold.salesThreshold > 0) shares.push((counters.rollingRevenue / threshold.salesThreshold) * 100);
  if (threshold.transactionThreshold !== null && threshold.transactionThreshold > 0) shares.push((counters.rollingTransactionCount / threshold.transactionThreshold) * 100);
  return shares.length === 0 ? null : Math.max(...shares);
}

const dollars = (cents: number): string => formatCurrency({ amount: cents, currencyCode: DEFAULT_CURRENCY_CODE });

/** The one sentence both the feed entry and the to-do carry, so the two never say different things. */
export function nexusNotificationSentence(stage: 'Warning' | 'Critical', stateCode: string, counters: NexusCounters, threshold: NexusThresholdFacts): string {
  const measured = `${dollars(counters.rollingRevenue)} over ${counted(counters.rollingTransactionCount, 'transaction')}`;
  const limit = [
    threshold.salesThreshold === null ? null : dollars(threshold.salesThreshold),
    threshold.transactionThreshold === null ? null : `${threshold.transactionThreshold} transactions`,
  ].filter((part): part is string => part !== null).join(' or ');
  if (stage === 'Critical') return `${stateCode}: economic nexus is established — ${measured} against ${limit}. Collection is owed from this date forward.`;
  return `${stateCode}: approaching economic nexus — ${measured} against ${limit}.`;
}

/** 🔨 A Critical crossing is never silently dismissed: both fields are written together or the crossing stays open. */
export function acknowledgementProblem(personId: string | null | undefined, acknowledgedAt: Date | null | undefined): string | null {
  if (!personId) return 'an acknowledgment names the person who accepted the crossing; choose who accepted it';
  if (!acknowledgedAt) return 'an acknowledgment carries the moment it was made; record the date and time it was accepted';
  return null;
}
